import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, CaretRight, Camera, Trash, Phone, ChalkboardTeacher } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KidAvatar } from "@/components/KidAvatar";
import { toast } from "sonner";

const EMPTY = { name: "", nis: "", student_number: "", gender: "Laki-laki", birth_place: "", birth_date: "", address: "", father_name: "", mother_name: "", parent_phone: "" };

export default function TeacherStudents() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get(`/teachers/${teacherId}`).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat data"));
  useEffect(() => { load(); }, [teacherId]); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => { setForm(EMPTY); setPhoto(null); setPreview(null); };
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(f.type)) { toast.error("Format harus JPG atau PNG"); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("Ukuran foto maksimal 3MB"); return; }
    setPhoto(f); setPreview(URL.createObjectURL(f));
  };
  const submit = async () => {
    if (!form.name || !form.nis) { toast.error("Nama & NIS wajib diisi"); return; }
    try {
      const { data: s } = await api.post("/students", { ...form, kelas: data.kelas, teacher_id: teacherId });
      if (photo) { const fd = new FormData(); fd.append("file", photo); await api.post(`/students/${s.id}/photo`, fd); }
      toast.success("Murid ditambahkan"); setOpen(false); reset(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Hapus data murid ini?")) return; await api.delete(`/students/${id}`); toast.success("Murid dihapus"); load(); };

  return (
    <div>
      <PageHeader title="Detail Umi Guru" />
      <div className="px-5 pt-4 pb-24">
        {!data ? <p className="text-center text-stone-400 mt-10">Memuat...</p> : (
          <>
            <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 flex items-center gap-4 mb-5" data-testid="teacher-info">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <ChalkboardTeacher size={28} weight="duotone" className="text-primary" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-stone-800">{data.name}</p>
                <p className="text-stone-500 text-sm flex items-center gap-1.5"><Phone size={15} weight="fill" /> {data.phone || "-"} • {data.kelas}</p>
              </div>
            </div>

            <p className="font-heading font-semibold text-stone-800 mb-2">Murid ({data.students.length})</p>
            <div className="space-y-2">
              {data.students.map((s) => (
                <div key={s.id} data-testid={`ts-student-${s.id}`} className="bg-white rounded-2xl border border-stone-200/60 p-3 flex items-center gap-3 shadow-sm">
                  <button onClick={() => navigate(`/siswa/${s.id}`)} className="flex items-center gap-3 flex-1 text-left active:scale-[0.98] transition-transform">
                    <KidAvatar gender={s.gender} size={44} src={s.avatar} />
                    <div className="flex-1">
                      <p className="font-semibold text-stone-800 text-sm">{s.name}</p>
                      <p className="text-stone-400 text-xs">{s.nis}{s.student_number ? ` • No. ${s.student_number}` : ""}</p>
                    </div>
                    <CaretRight size={16} className="text-stone-300" />
                  </button>
                  <button data-testid={`ts-del-${s.id}`} onClick={() => remove(s.id)} className="text-stone-300 hover:text-destructive"><Trash size={18} /></button>
                </div>
              ))}
              {data.students.length === 0 && <p className="text-center text-stone-400 mt-6">Belum ada murid di bawah Umi ini.</p>}
            </div>
          </>
        )}
      </div>

      {data && (
        <button data-testid="ts-add-student-btn" onClick={() => { reset(); setOpen(true); }}
          className="fixed bottom-8 right-1/2 translate-x-[9.5rem] md:right-auto md:translate-x-0 md:ml-[19rem] z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center active:scale-90 transition-transform">
          <Plus size={26} weight="bold" />
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => { setOpen(false); reset(); }}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[85vh] overflow-y-auto no-scrollbar">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Tambah Murid • {data.name}</h3>
              <button onClick={() => { setOpen(false); reset(); }}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="flex flex-col items-center mb-4">
              <div className="relative">
                <KidAvatar gender={form.gender} size={84} src={preview} className="ring-4 ring-stone-100" />
                <button data-testid="ts-photo-btn" onClick={() => fileRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow border-2 border-white"><Camera size={16} weight="fill" /></button>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick} />
              </div>
            </div>
            <div className="space-y-3">
              <Field label="Nama Lengkap" v={form.name} onChange={set("name")} testid="ts-name" />
              <Field label="NIS" v={form.nis} onChange={set("nis")} testid="ts-nis" />
              <Field label="Nomor Siswa" v={form.student_number} onChange={set("student_number")} testid="ts-number" />
              <div>
                <p className="text-xs text-stone-500 mb-1">Jenis Kelamin</p>
                <select data-testid="ts-gender" value={form.gender} onChange={set("gender")} className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary">
                  <option>Laki-laki</option><option>Perempuan</option>
                </select>
              </div>
              <Field label="Nama Ayah" v={form.father_name} onChange={set("father_name")} testid="ts-father" />
              <Field label="Nama Ibu" v={form.mother_name} onChange={set("mother_name")} testid="ts-mother" />
              <Field label="No. HP Orang Tua" v={form.parent_phone} onChange={set("parent_phone")} testid="ts-phone" />
              <Field label="Tempat Lahir" v={form.birth_place} onChange={set("birth_place")} testid="ts-birthplace" />
              <div>
                <p className="text-xs text-stone-500 mb-1">Tanggal Lahir</p>
                <input data-testid="ts-birthdate" type="date" value={form.birth_date} onChange={set("birth_date")} className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary" />
              </div>
              <Field label="Alamat Lengkap" v={form.address} onChange={set("address")} testid="ts-address" />
            </div>
            <button data-testid="ts-submit" onClick={submit} className="w-full bg-primary text-white font-semibold py-3.5 rounded-full mt-4 active:scale-95 transition-transform">Simpan</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, onChange, testid }) {
  return (
    <div>
      <p className="text-xs text-stone-500 mb-1">{label}</p>
      <input data-testid={testid} value={v} onChange={onChange} className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary" />
    </div>
  );
}
